import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useOrganizationStore } from '../stores/organizationStore'
import useChallengeStore from '../hooks/useChallenge'
import useFocusedPracticeStore from '../stores/focusedPracticeStore'
import { BookOpen, Target, Zap, ChevronRight, UserCheck, GraduationCap } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'
import api from '../services/api'
import { Avatar } from '../components/profile'
import { getErrorMessage } from '../utils/errorUtils.js'
import { getChallengeSessionPath } from '../utils/navigation'
import ConfirmDialog from '../components/ConfirmDialog'
import useConfirmDialog from '../hooks/useConfirmDialog'
import StrategyLevelTable from '../components/dashboard/StrategyLevelTable'
import availableStrategiesService from '../services/availableStrategiesService'
import GuardianDashboard from '../components/GuardianDashboard'
import CoachingActionsBanner from '../components/CoachingActionsBanner'
import { CombinedTrendChart, ShareProgressModal } from '../components/scores'
import ScoreSummaryTiles from '../components/dashboard/ScoreSummaryTiles'
import GreetingBar from '../components/dashboard/GreetingBar'
import PlanAdherenceBanner from '../components/dashboard/PlanAdherenceBanner'
import AIAdvisorCard from '../components/dashboard/AIAdvisorCard'
import {
  ActivityHeatmap,
  MotivationCard,
  useActivityHeatmap,
  checkIsActiveToday
} from '../components/heatmap'
import { usePerSubjectScores } from '../hooks/usePerSubjectScores'
import { usePlanAdherence } from '../hooks/usePlanAdherence'
import { useCombinedActualHistory } from '../hooks/useActualHistory'
// TODO: Re-enable when /api/user/assignments endpoint is implemented
// import StudentAssignments from '../components/StudentAssignments'
import StudentCoachMessages from '../components/StudentCoachMessages'
import StudentMessagesCard from '../components/StudentMessagesCard'
import WordOfTheDayWidget from '../components/dashboard/WordOfTheDayWidget'
import { createLogger } from '../utils/logger'

const logger = createLogger('Dashboard')

const Dashboard = () => {
  const {
    user,
    isLoading: authLoading,
    strategyLevels,
    fetchStrategyLevels,
    fetchMyCoaches
  } = useAuthStore()
  const { activeOrganization } = useOrganizationStore()
  const {
    currentChallenge,
    stages,
    isLoading,
    cancelChallenge,
    initializeChallengeStore,
    createAndStartChallenge
  } = useChallengeStore()
  const navigate = useNavigate()
  const { isOpen, config, closeDialog, handleConfirm, confirmDialog } = useConfirmDialog()

  const [strategyLevelsLoading, setStrategyLevelsLoading] = useState(true)
  const [availableStrategies, setAvailableStrategies] = useState([])

  // Focused Practice notification state
  const { fetchSessions: fetchFocusedPracticeSessions } = useFocusedPracticeStore()
  const [pendingFocusedPractice, setPendingFocusedPractice] = useState([])

  // Coach invitation state
  const [coachInvitations, setCoachInvitations] = useState([])

  // Pending "become a coach" admin invitation (fallback prompt in case the email link's
  // redirect-after-login didn't land the user on the acceptance page).
  const [coachAppInvitation, setCoachAppInvitation] = useState(null)

  // Track whether data loading has been triggered
  const [dataLoadTriggered, setDataLoadTriggered] = useState(false)

  // Per-subject score predictions for reflective dashboard
  const {
    loading: scoresLoading,
    math,
    readingWriting,
    combined,
    hasEnoughData: hasScoreData
  } = usePerSubjectScores({ autoFetch: true })

  // This week's study-plan adherence (actual vs recommended hours). Drives the
  // plan-deviation banner and supplies the student's goal for the greeting-bar score pill.
  const { data: planAdherence } = usePlanAdherence({ autoFetch: true })

  // Actual challenge score history (drives the trend chart's solid lines — same
  // source as the /progress page so the dashboard chart actually has data).
  const {
    math: mathActual,
    readingWriting: rwActual,
    loading: actualHistoryLoading
  } = useCombinedActualHistory()

  // The dashboard chart is a glanceable summary — window to ~60 days of
  // activity plus any forward projection. The full history lives on /progress
  // via "View Details". The window is anchored to the most recent *actual*
  // score (not `Date.now()`) so a student who hasn't practiced in months still
  // sees their last ~60 days of history instead of an empty chart / a wrong
  // "complete more challenges" message. Memoized so an unrelated re-render
  // doesn't produce fresh array references (which would needlessly re-render
  // the chart) and so `Date.now()` is only sampled when source data changes.
  // Plot the FULL available score history (no recent-only window): a student whose activity is
  // older or sparse-recent should still see their whole trend instead of a misleading
  // "complete more challenges" empty state. The time axis may be non-continuous (gaps between
  // sessions) — that's expected. Memoized so unrelated re-renders don't churn array references.
  const { dashboardMathActual, dashboardRwActual, dashboardMathPredicted, dashboardRwPredicted } =
    useMemo(
      () => ({
        dashboardMathActual: mathActual?.history || [],
        dashboardRwActual: rwActual?.history || [],
        dashboardMathPredicted: math?.trend?.history || [],
        dashboardRwPredicted: readingWriting?.trend?.history || []
      }),
      [mathActual?.history, rwActual?.history, math?.trend?.history, readingWriting?.trend?.history]
    )

  // The dashboard intentionally fetches actual history via its own
  // `useCombinedActualHistory` call (separate from /progress). An app-level
  // request cache was considered and explicitly dropped per product decision;
  // instead the global rate limit is raised via env config in
  // `server/server.js` (RATE_LIMIT_* — see the authenticated limiter ~L656).
  // Keep the hooks simple and let each page fetch independently.
  //
  // Require at least two points on a line before calling it a "trend" — a
  // single dot per series isn't worth a chart.
  const hasTrendData =
    dashboardMathActual.length >= 2 ||
    dashboardRwActual.length >= 2 ||
    dashboardMathPredicted.length >= 2 ||
    dashboardRwPredicted.length >= 2

  // No predictions yet *and* no actual score history to plot — show the
  // "complete more challenges" empty state instead of an empty chart.
  const hasNoScoreHistory =
    !hasScoreData && !(mathActual?.history?.length || rwActual?.history?.length)

  // State for share modal
  const [showShareModal, setShowShareModal] = useState(false)

  // Activity heatmap data for MotivationCard (guardians will early return, so fetch is fine)
  const { activities: activityData, stats: activityStats } = useActivityHeatmap({
    autoFetch: !authLoading && !!user
  })

  // Derived values for the greeting bar / strategy header. Declared here (with
  // the other hooks) so they run before any early return — Rules of Hooks.
  // todayLabel is effectively constant for the page-load lifetime.
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
    []
  )
  const currentStreak = activityStats?.currentStreak || 0
  const longestStreak = activityStats?.longestStreak || 0
  const startedStrategyCount = useMemo(
    () =>
      availableStrategies.filter((s) =>
        strategyLevels.some((l) => l.strategyType === (s.strategyType || s.type))
      ).length,
    [availableStrategies, strategyLevels]
  )

  // Check if user should see guardian dashboard
  // Priority: 1) accountType === 'guardian', 2) role === 'guardian' in active organization
  const isGuardian =
    !authLoading &&
    user &&
    (user.accountType === 'guardian' ||
      (activeOrganization &&
        user.organizations?.find((org) => org.id === activeOrganization.id)?.role === 'guardian'))

  const loadStrategyLevels = async () => {
    try {
      setStrategyLevelsLoading(true)
      // Load both user strategy levels and available strategies in parallel
      const [, strategies] = await Promise.all([
        fetchStrategyLevels(),
        availableStrategiesService.getAvailableStrategies()
      ])
      setAvailableStrategies(strategies)
    } catch (error) {
      logger.error('Error loading strategy levels:', error)
    } finally {
      setStrategyLevelsLoading(false)
    }
  }

  // Load pending focused practice sessions
  const loadPendingFocusedPractice = async () => {
    try {
      const result = await fetchFocusedPracticeSessions()
      if (result.success) {
        // Filter for sessions that are pending or in progress
        const pending = (result.data?.sessions || []).filter(
          (s) => s.status === 'pending' || s.status === 'pre_practice' || s.status === 'in_progress'
        )
        setPendingFocusedPractice(pending)
      }
    } catch (error) {
      logger.error('Error loading focused practice:', error)
    }
  }

  // Load pending coach invitations (coaches who want to coach this user)
  const loadCoachInvitations = async () => {
    try {
      const response = await api.get('/user/coach-invitations')
      if (response.data.success) {
        setCoachInvitations(response.data.received || [])
      }
    } catch (error) {
      logger.error('Error loading coach invitations:', error)
    }
  }

  // Load a pending admin invitation to become a coach (distinct from coach-of-student invites
  // above). Surfaced as a dashboard prompt; acting on it reuses the AcceptCoachInvitation page.
  const loadCoachAppInvitation = async () => {
    try {
      const response = await api.get('/user/coach-application')
      const app = response.data?.application
      const notExpired = !app?.invitationExpiresAt || new Date(app.invitationExpiresAt) > new Date()
      if (
        app?.status === 'pending' &&
        app?.applicationType === 'invite' &&
        app?.invitationToken &&
        notExpired
      ) {
        setCoachAppInvitation(app)
      } else {
        setCoachAppInvitation(null)
      }
    } catch (error) {
      logger.error('Error loading coach application invitation:', error)
    }
  }

  // Handle accepting coach invitation
  const handleAcceptInvitation = async (relationshipId) => {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/accept`)
      if (response.data.success) {
        toast.success('Coach invitation accepted!')
        loadCoachInvitations()
        fetchMyCoaches() // Update authStore for sidebar
      }
    } catch (error) {
      logger.error('Error accepting invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to accept invitation')
    }
  }

  // Handle declining coach invitation
  const handleDeclineInvitation = async (relationshipId) => {
    try {
      const response = await api.post(`/user/coach-invitations/${relationshipId}/decline`)
      if (response.data.success) {
        toast.success('Invitation declined')
        loadCoachInvitations()
      }
    } catch (error) {
      logger.error('Error declining invitation:', error)
      toast.error(error.response?.data?.message || 'Failed to decline invitation')
    }
  }

  // Load dashboard data once, when the user is available (skip for guardians).
  // The loader functions are intentionally not in the dep array: the effect is
  // a one-shot guarded by `dataLoadTriggered`, so it cannot loop, and the
  // linter's exhaustive-deps rule confirms no missing dependency here (the
  // previous eslint-disable directive was reported unused and removed).
  useEffect(() => {
    if (!authLoading && user && !dataLoadTriggered && !isGuardian) {
      setDataLoadTriggered(true)
      // Add a small delay to ensure session cookie is properly set
      setTimeout(() => {
        // Initialize challenge store first to check for active challenges
        initializeChallengeStore().catch((error) => {
          logger.error('❌ Failed to initialize challenge store:', error)
        })

        // Load strategy levels
        loadStrategyLevels()

        // Load pending focused practice sessions
        loadPendingFocusedPractice()

        // Load pending coach invitations
        loadCoachInvitations()

        // Load a pending admin invitation to become a coach (fallback prompt)
        loadCoachAppInvitation()
      }, 100)
    }
  }, [authLoading, user, dataLoadTriggered, isGuardian])

  // Refresh strategy levels when navigating back to dashboard (skip for guardians)
  useEffect(() => {
    if (isGuardian) return // Skip for guardians

    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        loadStrategyLevels()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [user, isGuardian])

  // Early return for guardians - MUST be after all hooks
  if (isGuardian) {
    return <GuardianDashboard />
  }

  const handleCancelChallenge = async () => {
    const confirmed = await confirmDialog({
      title: 'Cancel Challenge',
      message: 'Are you sure you want to cancel this challenge? This action cannot be undone.',
      confirmText: 'Yes, Cancel',
      cancelText: 'Keep Challenge'
    })

    if (confirmed) {
      const result = await cancelChallenge()
      if (result.success) {
        toast.success('Challenge cancelled successfully')
      } else {
        toast.error(getErrorMessage(result.error))
      }
    }
  }

  const handleViewStrategy = (strategyType) => {
    navigate(`/strategy/${strategyType}`)
  }

  const handleStartStrategyChallenge = async (strategyType) => {
    // Check if there's already an active challenge
    if (currentChallenge) {
      toast.error('You already have an active challenge. Please complete or cancel it first.')
      navigate(getChallengeSessionPath(currentChallenge.id))
      return
    }

    try {
      // For SAT strategies, only send strategyType
      const challengeData = {
        strategyType: strategyType
      }

      logger.debug('🚀 Starting challenge for strategy:', strategyType)
      const result = await createAndStartChallenge(challengeData)

      if (result.success) {
        toast.success('Challenge started successfully!')
        navigate(getChallengeSessionPath(result.challenge?.id), { replace: true })
      } else {
        logger.error('❌ Failed to start challenge:', result.error)
        toast.error(result.error || 'Failed to start challenge')
      }
    } catch (error) {
      logger.error('Error starting challenge:', error)
      toast.error('Failed to start challenge')
    }
  }

  if (isLoading || authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* ===== Notification banners (kept at the very top of the screen) ===== */}

      {/* Tutoring agreements awaiting the user's signature */}
      <CoachingActionsBanner />

      {/* Invitation to become a coach (admin invite) */}
      {coachAppInvitation && (
        <div className="bg-gradient-to-r from-primary-50 to-primary-100/60 dark:from-primary-900/20 dark:to-primary-800/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl shadow-md shrink-0">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  You've been invited to become a coach!
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  Review the invitation to accept or decline.
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                navigate(`/coach/accept-invitation/${coachAppInvitation.invitationToken}`)
              }
              className="btn btn-sm btn-primary shrink-0"
            >
              Review Invitation
            </button>
          </div>
        </div>
      )}

      {/* Coach invitation */}
      {coachInvitations.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl shadow-md">
                <UserCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {coachInvitations.length === 1
                    ? 'You have a coaching invitation!'
                    : `${coachInvitations.length} coaching invitations`}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  A coach wants to help you on your learning journey
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {coachInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      type={invitation.coach?.avatarType}
                      seed={invitation.coach?.avatarSeed}
                      style={invitation.coach?.avatarStyle}
                      name={`${invitation.coach?.firstName || ''} ${invitation.coach?.lastName || ''}`}
                      publicName={invitation.coach?.publicName}
                      size="sm"
                    />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {invitation.coach?.firstName} {invitation.coach?.lastName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">wants to coach you</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAcceptInvitation(invitation.id)}
                      className="btn btn-sm btn-primary"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleDeclineInvitation(invitation.id)}
                      className="btn btn-sm btn-secondary"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Focused practice */}
      {pendingFocusedPractice.length > 0 && (
        <div className="bg-gradient-to-r from-primary-50 to-accent-50 dark:from-primary-900/20 dark:to-accent-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="p-2.5 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl shadow-md shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {pendingFocusedPractice.length === 1
                  ? 'You have a focused practice session waiting!'
                  : `${pendingFocusedPractice.length} focused practice sessions ready`}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {pendingFocusedPractice.length === 1
                  ? `Continue practicing ${pendingFocusedPractice[0].targetDomain} to boost your score`
                  : 'Complete these to strengthen your weak areas and improve your scores'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="hidden md:flex gap-1.5">
              {pendingFocusedPractice.slice(0, 2).map((session) => (
                <span
                  key={session.id}
                  className="px-2 py-1 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-700"
                >
                  {session.targetDomain}
                </span>
              ))}
              {pendingFocusedPractice.length > 2 && (
                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 rounded-full">
                  +{pendingFocusedPractice.length - 2}
                </span>
              )}
            </div>
            <button
              onClick={() => navigate('/focused-practice')}
              className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary-600 to-accent-600 hover:from-primary-700 hover:to-accent-700 text-white text-sm font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
            >
              <Target className="w-4 h-4" />
              <span>Practice Now</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Greeting bar — compact: avatar, name, date, level, streak + active-challenge controls */}
      <GreetingBar
        user={user}
        todayLabel={todayLabel}
        currentStreak={currentStreak}
        longestStreak={longestStreak}
        projectedScore={combined?.predictedScore ?? null}
        currentScore={combined?.currentScore ?? null}
        goalScore={planAdherence?.goalScore ?? null}
        onSetGoal={() => navigate('/progress')}
        currentChallenge={currentChallenge}
        stages={stages}
        onCancelChallenge={handleCancelChallenge}
        onContinueChallenge={() => navigate(getChallengeSessionPath(currentChallenge?.id))}
        onStartNewChallenge={() => navigate('/challenge-type')}
      />

      {/* Plan-deviation banner: actual vs recommended weekly study hours. Only shows when the
          student has a goal + plan. */}
      {planAdherence?.hasGoal && planAdherence?.hasPlan && (
        <PlanAdherenceBanner
          data={planAdherence}
          onStartPractice={() => navigate('/challenge-type')}
        />
      )}

      {/* Word of the Day — top banner (UC-121) */}
      <WordOfTheDayWidget variant="banner" />

      {/* ===== Score summary tiles: Math · Reading & Writing · Total ===== */}
      <ScoreSummaryTiles
        math={math}
        readingWriting={readingWriting}
        combined={combined}
        loading={scoresLoading}
      />

      {/* ===== Your SAT Journey (score trend) ===== */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Your SAT Journey
            </h3>
          </div>
          <button
            onClick={() => navigate('/progress')}
            className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium flex items-center gap-1"
          >
            View Details
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="card-body">
          {scoresLoading || actualHistoryLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="sm" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading your scores...</span>
            </div>
          ) : hasNoScoreHistory ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 font-medium">
                Complete more challenges to see your predicted SAT scores
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                We need at least 10 questions answered to generate predictions
              </p>
            </div>
          ) : hasTrendData ? (
            <CombinedTrendChart
              mathActualHistory={dashboardMathActual}
              rwActualHistory={dashboardRwActual}
              mathPredictedHistory={dashboardMathPredicted}
              rwPredictedHistory={dashboardRwPredicted}
              mathUncertainty={math?.uncertaintyBand}
              rwUncertainty={readingWriting?.uncertaintyBand}
              height={220}
              showConfidenceBands={true}
              showProjections={true}
              fitContainer={true}
              onShare={() => setShowShareModal(true)}
            />
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
              Your score trend chart will appear here after a few more challenges.
            </p>
          )}
        </div>
      </div>

      {/* ===== Strategy Levels & Progress (compact table) ===== */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Strategy Levels & Progress
          </h3>
          {!strategyLevelsLoading && availableStrategies.length > 0 && (
            <span className="text-xs text-gray-400">
              {startedStrategyCount} of {availableStrategies.length} started
            </span>
          )}
        </div>
        {strategyLevelsLoading ? (
          <div className="card-body flex items-center justify-center py-8">
            <LoadingSpinner size="sm" />
            <span className="ml-2 text-gray-600 dark:text-gray-400">
              Loading strategy levels...
            </span>
          </div>
        ) : availableStrategies.length === 0 ? (
          <div className="card-body text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No strategies available</p>
          </div>
        ) : (
          <StrategyLevelTable
            strategies={availableStrategies}
            strategyLevels={strategyLevels}
            currentChallenge={currentChallenge}
            onStart={handleStartStrategyChallenge}
            onContinue={() => navigate(getChallengeSessionPath(currentChallenge?.id))}
            onView={handleViewStrategy}
            publicName={user?.publicName}
          />
        )}
      </div>

      {/* ===== Activity heatmap + motivation ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ActivityHeatmap activities={activityData} stats={activityStats} className="" />
        <MotivationCard
          stats={{
            ...activityStats,
            isActiveToday: checkIsActiveToday(activityData)
          }}
          className=""
        />
      </div>

      {/* AI Study Advisor (self-hides unless the feature is on and the student opted in) */}
      <AIAdvisorCard />

      {/* Coach messages & direct messaging */}
      {/* TODO: Re-enable StudentAssignments when /api/user/assignments endpoint exists */}
      <StudentCoachMessages />
      <StudentMessagesCard />

      {/* Confirmation Dialog */}
      <ConfirmDialog isOpen={isOpen} onClose={closeDialog} onConfirm={handleConfirm} {...config} />

      {/* Share Progress Modal */}
      <ShareProgressModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        username={user?.publicName || user?.firstName}
      />
    </div>
  )
}

export default Dashboard
